/**
 * Attachment tile (design README shared kit) — used on tracking (public only) + staff detail.
 * Thumbnail (image) or file glyph (pdf/doc), filename, a type chip, and — on the staff side — the
 * public|internal visibility marker. Clicking an image opens the lightbox.
 */
import type { AttachmentView } from "../api/types.js";
import { VisibilityMarker } from "./VisibilityMarker.js";
import { FileIcon } from "./icons.js";

const KIND_LABEL: Record<AttachmentView["kind"], string> = {
  "resident-photo": "Photo",
  "site-photo": "Site photo",
  "letter-sent": "Letter sent",
  "certified-mail": "Certified mail",
  document: "Document",
  other: "File",
};

export function AttachmentTile({
  attachment,
  showVisibility = false,
  onOpen,
}: {
  attachment: AttachmentView;
  showVisibility?: boolean;
  onOpen?: (a: AttachmentView) => void;
}): JSX.Element {
  const { url, filename, kind, isImage, visibility, trackingNumber } = attachment;
  const clickable = isImage && Boolean(onOpen);

  const inner = (
    <>
      <span className="td-tile__thumb" aria-hidden={!clickable}>
        {isImage && url ? <img src={url} alt="" /> : <FileIcon width={26} height={26} />}
      </span>
      <span className="td-tile__meta">
        <span className="td-tile__name" title={filename}>
          {filename}
        </span>
        <span className="td-tile__chips">
          <span className="td-tile__kind">{KIND_LABEL[kind]}</span>
          {showVisibility ? <VisibilityMarker visibility={visibility} /> : null}
        </span>
        {trackingNumber ? (
          <span className="td-tile__tracking">Tracking: {trackingNumber}</span>
        ) : null}
      </span>
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        className="td-tile td-tile--button"
        onClick={() => onOpen?.(attachment)}
        aria-label={`Open ${filename}`}
      >
        {inner}
      </button>
    );
  }
  return <div className="td-tile">{inner}</div>;
}
